// ============================================================
// src/shack/shackCalibration.js
// Calibración, grids y helper rect del Shack 3D
// ============================================================

import {
  T,
  ROOM,
  GRID_RES,
  CAL_STORAGE_KEY,
} from './shackConfig.js';

import {
  surfacePoint,
  surfaceFrameAt,
  projectWorldToStage,
} from './shackSurface.js';

// ------------------------------------------------------------
// CALIBRACIÓN BASE
// ------------------------------------------------------------

export function defaultCalibration() {
  return {
    trasera: {
      tl: { x: -ROOM.trasera.ancho, y: ROOM.alto, z: 0 },
      tr: { x: 0,                   y: ROOM.alto, z: 0 },
      br: { x: 0,                   y: 0,         z: 0 },
      bl: { x: -ROOM.trasera.ancho, y: 0,         z: 0 },
    },
    lateral: {
      tl: { x: 0, y: ROOM.alto, z: 0 },
      tr: { x: 0, y: ROOM.alto, z: ROOM.lateral.ancho },
      br: { x: 0, y: 0,         z: ROOM.lateral.ancho },
      bl: { x: 0, y: 0,         z: 0 },
    },
  };
}

export function cargarCalibracion() {
  try {
    const raw = localStorage.getItem(CAL_STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data?.trasera?.tl || !data?.lateral?.tl) return null;

    return data;
  } catch {
    return null;
  }
}

export function guardarCalibracionLocal(calibration) {
  try {
    localStorage.setItem(CAL_STORAGE_KEY, JSON.stringify(calibration));
  } catch (err) {
    console.warn('[Shack3D] No se pudo guardar calibración local:', err);
  }
}

export function imprimirYGuardarCalibracion(calibration, mostrarToast) {
  const txt = JSON.stringify({
    trasera: calibration.trasera,
    lateral: calibration.lateral,
  }, null, 2);

  guardarCalibracionLocal(calibration);
  console.log('[Shack3D] CALIBRACION:\n', txt);

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(txt).catch(() => {});
  }

  if (typeof mostrarToast === 'function') {
    mostrarToast('Calibración guardada.', 'ok');
  }
}

// ------------------------------------------------------------
// GRID 3D
// ------------------------------------------------------------

export function crearGridObject(wall, cols, rows) {
  const THREE = T();

  const group = new THREE.Group();

  const colorGrid = wall === 'trasera' ? 0x00d5ff : 0xffb300;
  const colorBorder = wall === 'trasera' ? 0x00ffff : 0xff6a00;

  const lineMat = new THREE.LineBasicMaterial({
    color: colorGrid,
    transparent: true,
    opacity: 0.62,
    depthWrite: false,
  });

  const borderMat = new THREE.LineBasicMaterial({
    color: colorBorder,
    transparent: true,
    opacity: 0.98,
    depthWrite: false,
  });

  const lineSegments = new THREE.LineSegments(new THREE.BufferGeometry(), lineMat);
  const border = new THREE.LineLoop(new THREE.BufferGeometry(), borderMat);

  group.add(lineSegments);
  group.add(border);

  return { wall, cols, rows, group, lineSegments, border };
}

export function rebuildGrid({ calibration, gridObjects, wall }) {
  const THREE = T();
  const obj = gridObjects?.[wall];
  if (!obj) return;

  const { cols, rows } = obj;
  const pts = [];

  for (let i = 0; i <= cols; i++) {
    const u = i / cols;
    for (let j = 0; j < rows; j++) {
      const v1 = j / rows;
      const v2 = (j + 1) / rows;
      const p1 = surfacePoint(calibration, wall, u, v1);
      const p2 = surfacePoint(calibration, wall, u, v2);
      pts.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
    }
  }

  for (let j = 0; j <= rows; j++) {
    const v = j / rows;
    for (let i = 0; i < cols; i++) {
      const u1 = i / cols;
      const u2 = (i + 1) / cols;
      const p1 = surfacePoint(calibration, wall, u1, v);
      const p2 = surfacePoint(calibration, wall, u2, v);
      pts.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));

  if (obj.lineSegments.geometry) obj.lineSegments.geometry.dispose();
  obj.lineSegments.geometry = geo;

  const c = calibration[wall];
  const borderPts = [
    c.tl.x, c.tl.y, c.tl.z,
    c.tr.x, c.tr.y, c.tr.z,
    c.br.x, c.br.y, c.br.z,
    c.bl.x, c.bl.y, c.bl.z,
  ];

  const borderGeo = new THREE.BufferGeometry();
  borderGeo.setAttribute('position', new THREE.Float32BufferAttribute(borderPts, 3));

  if (obj.border.geometry) obj.border.geometry.dispose();
  obj.border.geometry = borderGeo;
}

export function crearGrids(scene) {
  const gridObjects = {
    trasera: crearGridObject('trasera', GRID_RES.trasera.cols, GRID_RES.trasera.rows),
    lateral: crearGridObject('lateral', GRID_RES.lateral.cols, GRID_RES.lateral.rows),
  };

  scene.add(gridObjects.trasera.group);
  scene.add(gridObjects.lateral.group);

  gridObjects.trasera.group.visible = false;
  gridObjects.lateral.group.visible = false;

  return gridObjects;
}

// ------------------------------------------------------------
// HELPER RECT — borde de selección (4 barras planas por Group)
// ------------------------------------------------------------

// Grosor del borde en world-units y offset Z hacia cámara
const SEL_BAR_T = 0.006;
const SEL_BAR_Z = 0.004;

// Posiciona las 4 barras de un group en torno a un item
function _posicionarBordeGrupo(group, surf, w, h) {
  const T = SEL_BAR_T;
  const Z = SEL_BAR_Z;
  group.visible = true;
  group.position.copy(surf.center);
  group.quaternion.copy(surf.quaternion);
  const b = group.children;
  // top
  b[0].position.set(0,  h / 2 + T / 2, Z); b[0].scale.set(w + 2 * T, T, 1);
  // bottom
  b[1].position.set(0, -(h / 2 + T / 2), Z); b[1].scale.set(w + 2 * T, T, 1);
  // left
  b[2].position.set(-(w / 2 + T / 2), 0, Z); b[2].scale.set(T, h, 1);
  // right
  b[3].position.set( w / 2 + T / 2, 0, Z); b[3].scale.set(T, h, 1);
}

// Crea un Group de 4 barras MeshBasic con depthTest desactivado
function _crearBordeGroup(scene, color, renderOrder) {
  const THREE = T();
  const geo = new THREE.PlaneGeometry(1, 1);
  const mat = new THREE.MeshBasicMaterial({
    color,
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const group = new THREE.Group();
  group.visible = false;
  for (let i = 0; i < 4; i++) {
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = renderOrder;
    group.add(mesh);
  }
  scene.add(group);
  return group;
}

export function crearHelperRect(scene) {
  // cyan brillante para el item primario seleccionado
  return _crearBordeGroup(scene, 0x00e5ff, 999);
}

export function actualizarHelperRect({ helperRect, calibration, item }) {
  if (!helperRect || !item) return;
  const surf = surfaceFrameAt(calibration, item.wall, item.u, item.v, item.w, item.h);
  _posicionarBordeGrupo(helperRect, surf, item.w, item.h);
}

export function ocultarHelperRect(helperRect) {
  if (helperRect) helperRect.visible = false;
}

// ------------------------------------------------------------
// MULTI-SELECT RECTS — pool de bordes para items secundarios
// ------------------------------------------------------------

const MULTI_RECT_POOL = 30;

export function crearMultiSelectRects(scene) {
  const THREE = T();
  // geometría y material compartidos en todo el pool
  const geo = new THREE.PlaneGeometry(1, 1);
  const mat = new THREE.MeshBasicMaterial({
    color: 0x64b5f6, // azul claro — distinguible del cyan primario
    depthTest: false,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const rects = [];
  for (let i = 0; i < MULTI_RECT_POOL; i++) {
    const group = new THREE.Group();
    group.visible = false;
    for (let j = 0; j < 4; j++) {
      const mesh = new THREE.Mesh(geo, mat);
      mesh.renderOrder = 998;
      group.add(mesh);
    }
    scene.add(group);
    rects.push(group);
  }
  return rects;
}

export function actualizarMultiSelectRects({ rects, calibration, items, selectedKeys, primaryKey }) {
  if (!rects?.length || !calibration) return;

  const keysToShow = [...(selectedKeys ?? [])].filter((k) => k !== primaryKey);

  rects.forEach((group, i) => {
    const key = keysToShow[i];
    if (!key) { group.visible = false; return; }
    const item = items.find((it) => it.shackKey === key);
    if (!item) { group.visible = false; return; }
    const surf = surfaceFrameAt(calibration, item.wall, item.u, item.v, item.w, item.h);
    _posicionarBordeGrupo(group, surf, item.w, item.h);
  });
}

// ------------------------------------------------------------
// GRID VISUAL
// ------------------------------------------------------------

export function mostrarGrid(gridObjects, calibrationMode, wall) {
  if (!gridObjects?.trasera || !gridObjects?.lateral) return;

  if (calibrationMode) {
    gridObjects.trasera.group.visible = true;
    gridObjects.lateral.group.visible = true;
    return;
  }

  gridObjects.trasera.group.visible = wall === 'trasera';
  gridObjects.lateral.group.visible = wall === 'lateral';
}

export function ocultarGrid(gridObjects, calibrationMode) {
  if (!gridObjects?.trasera || !gridObjects?.lateral) return;

  if (calibrationMode) {
    gridObjects.trasera.group.visible = true;
    gridObjects.lateral.group.visible = true;
    return;
  }

  gridObjects.trasera.group.visible = false;
  gridObjects.lateral.group.visible = false;
}

// ------------------------------------------------------------
// UI DOM DE CALIBRACIÓN
// ------------------------------------------------------------

export function crearDOMCalibrationUI({
  svg,
  overlay,
  calibrationMode,
  drag,
  handleEls,
  outlineEls,
  onHandlePointerDown,
}) {
  const walls = ['trasera', 'lateral'];
  const corners = ['tl', 'tr', 'br', 'bl'];

  walls.forEach((wall) => {
    const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    poly.setAttribute('class', 'shack-cal-outline');
    svg.appendChild(poly);
    outlineEls[wall] = poly;

    handleEls[wall] = {};

    corners.forEach((cornerKey) => {
      const el = document.createElement('div');
      el.className = 'shack-cal-handle';
      el.style.display = 'none';
      el.style.touchAction = 'none';

      el.addEventListener('pointerdown', (ev) => {
        if (!calibrationMode()) return;
        ev.preventDefault();
        ev.stopPropagation();

        try {
          el.setPointerCapture?.(ev.pointerId);
        } catch {}

        drag.activo = true;
        drag.tipo = 'handle';
        drag.wall = wall;
        drag.cornerKey = cornerKey;

        if (typeof onHandlePointerDown === 'function') {
          onHandlePointerDown(ev, wall, cornerKey);
        }
      });

      overlay.appendChild(el);
      handleEls[wall][cornerKey] = el;
    });
  });
}

export function actualizarDOMCalibrationUI({
  svg,
  calibration,
  calibrationMode,
  handleEls,
  outlineEls,
  camera,
  imageView,
}) {
  if (!svg) return;

  const walls = ['trasera', 'lateral'];
  const corners = ['tl', 'tr', 'br', 'bl'];

  walls.forEach((wall) => {
    const pts = [];

    corners.forEach((cornerKey) => {
      const p3 = calibration[wall][cornerKey];
      const p2 = projectWorldToStage(camera, imageView, p3);

      const el = handleEls[wall]?.[cornerKey];
      if (el) {
        el.style.display = calibrationMode ? 'block' : 'none';
        el.style.left = `${p2.x}px`;
        el.style.top = `${p2.y}px`;
      }

      pts.push(`${p2.x},${p2.y}`);
    });

    const poly = outlineEls[wall];
    if (poly) {
      poly.style.display = calibrationMode ? 'block' : 'none';
      poly.setAttribute('points', pts.join(' '));
    }
  });
}

// ------------------------------------------------------------
// WALL PICK MESHES
// ------------------------------------------------------------

function buildPickGeo(THREE, calibration, wall, margin = 1.3) {
  const c = calibration[wall];

  const cx = (c.tl.x + c.tr.x + c.br.x + c.bl.x) / 4;
  const cy = (c.tl.y + c.tr.y + c.br.y + c.bl.y) / 4;
  const cz = (c.tl.z + c.tr.z + c.br.z + c.bl.z) / 4;

  function exp(pt) {
    return [
      cx + (pt.x - cx) * margin,
      cy + (pt.y - cy) * margin,
      cz + (pt.z - cz) * margin,
    ];
  }

  const tl = exp(c.tl);
  const tr = exp(c.tr);
  const br = exp(c.br);
  const bl = exp(c.bl);

  // Dos triángulos que cubren el quad calibrado (con margen)
  const pos = new Float32Array([
    ...tl, ...tr, ...br,
    ...tl, ...br, ...bl,
  ]);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return geo;
}

export function crearWallPickMeshes(scene, calibration) {
  const THREE = T();

  const matProxy = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const trasera = new THREE.Mesh(buildPickGeo(THREE, calibration, 'trasera'), matProxy);
  trasera.userData.wall = 'trasera';
  scene.add(trasera);

  const lateral = new THREE.Mesh(buildPickGeo(THREE, calibration, 'lateral'), matProxy);
  lateral.userData.wall = 'lateral';
  scene.add(lateral);

  return { trasera, lateral };
}

export function actualizarWallPickMeshes(pickMeshes, calibration) {
  const THREE = T();
  if (!pickMeshes || !calibration) return;

  for (const wall of ['trasera', 'lateral']) {
    const mesh = pickMeshes[wall];
    if (!mesh) continue;
    const oldGeo = mesh.geometry;
    mesh.geometry = buildPickGeo(THREE, calibration, wall);
    oldGeo?.dispose();
  }
}

// ------------------------------------------------------------
// UTILIDAD DE ACTUALIZACIÓN COMPLETA
// ------------------------------------------------------------

export async function actualizarVisualCalibracion({
  calibration,
  gridObjects,
  pickWallMeshes,
  calibrationMode,
  actualizarDOM,
  actualizarLayoutStage,
  centrarStageSiCorresponde,
  renderizarItems,
  thumbs,
  relayout = true,
  recenter = false,
  rerenderItems = false,
}) {
  rebuildGrid({ calibration, gridObjects, wall: 'trasera' });
  rebuildGrid({ calibration, gridObjects, wall: 'lateral' });
  actualizarWallPickMeshes(pickWallMeshes, calibration);

  if (typeof actualizarDOM === 'function') {
    actualizarDOM();
  }

  if (gridObjects?.trasera) gridObjects.trasera.group.visible = calibrationMode;
  if (gridObjects?.lateral) gridObjects.lateral.group.visible = calibrationMode;

  if (relayout && typeof actualizarLayoutStage === 'function') {
    actualizarLayoutStage();
  }

  if (recenter && typeof centrarStageSiCorresponde === 'function') {
    centrarStageSiCorresponde();
  }

  if (rerenderItems && typeof renderizarItems === 'function') {
    await renderizarItems(thumbs);
  }
}